export const PLAYBACK_MODES = Object.freeze({
  balanced: Object.freeze({ label: "Balanced", normal: 1.25, fast: 1.5 }),
  faster: Object.freeze({ label: "Faster", normal: 1.5, fast: 1.75 }),
  hardcore: Object.freeze({ label: "Hardcore", normal: 2, fast: 2 }),
});
export const DEFAULT_PLAYBACK_MODE = "balanced";
export const DEFAULT_SPEED_LIMITS = Object.freeze({
  normal: PLAYBACK_MODES[DEFAULT_PLAYBACK_MODE].normal,
  fast: PLAYBACK_MODES[DEFAULT_PLAYBACK_MODE].fast,
});
export const YOUTUBE_SPEED_OPTIONS = Object.freeze([0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]);
export const DEFAULT_ADAPTIVE_RATES = Object.freeze([1, 1.25, 1.5, 1.75, 2]);

export function adaptiveEstimationRates(availableRates) {
  const reported = Array.isArray(availableRates)
    ? [...new Set(availableRates.filter((rate) => Number.isFinite(rate) && rate > 0))].sort((a, b) => a - b)
    : [];
  // The iframe commonly reports only 1× until playback begins. That is not a
  // reliable capability result, so calculate mode durations from our supported
  // preset rates until YouTube returns a real multi-rate list.
  return reported.some((rate) => rate > 1) ? reported : [...DEFAULT_ADAPTIVE_RATES];
}

export function speedLimitsForMode(mode) {
  const selected = PLAYBACK_MODES[mode] || PLAYBACK_MODES[DEFAULT_PLAYBACK_MODE];
  return { normal: selected.normal, fast: selected.fast };
}

export function formatDuration(seconds, empty = "—") {
  if (!Number.isFinite(seconds) || seconds < 0) return empty;
  const rounded = Math.round(seconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remainingSeconds = rounded % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
    : `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function formatTotalMinutes(seconds, empty = "—") {
  if (!Number.isFinite(seconds) || seconds < 0) return empty;
  return `${Math.round(seconds / 60)} min`;
}

export function resolveSupportedRate(requestedRate, availableRates = [1]) {
  const rates = [...new Set(availableRates.filter((rate) => Number.isFinite(rate)))].sort((a, b) => a - b);
  if (rates.includes(requestedRate)) return requestedRate;
  return rates.reduce((closest, rate) => {
    const difference = Math.abs(rate - requestedRate);
    const closestDifference = Math.abs(closest - requestedRate);
    if (difference < closestDifference) return rate;
    if (difference === closestDifference && Math.abs(rate - 1) < Math.abs(closest - 1)) return rate;
    return closest;
  }, rates[0] ?? 1);
}

export function calculateAdaptiveDuration(segments, { skipSponsors = false } = {}) {
  return segments.reduce((total, segment) => {
    if (skipSponsors && segment.sponsor) return total;
    return total + (segment.end - segment.start) / segment.speed;
  }, 0);
}

export function applyPacingPreference(plan, speedLimits, availableRates, mode = DEFAULT_PLAYBACK_MODE) {
  if (!plan) return null;
  const limits = speedLimits && typeof speedLimits === "object" ? speedLimits : DEFAULT_SPEED_LIMITS;
  const segments = plan.segments.map((segment) => {
    const requested = resolveSupportedRate(
      segment.category === "fast" ? limits.fast : limits.normal,
      availableRates,
    );
    const ceiling = Number.isFinite(segment.maxPlaybackRate)
      ? Math.min(requested, segment.maxPlaybackRate)
      : requested;
    const supportedRates = [...new Set((availableRates || []).filter((rate) => Number.isFinite(rate)))].sort((a, b) => a - b);
    // A pace ceiling is a safety limit, so choose a supported rate below it rather than rounding up.
    const safeRate = supportedRates.filter((rate) => rate <= ceiling).at(-1);
    let speed = safeRate ?? Math.min(1, ceiling);
    // Named modes are explicit ranges: normal sections use the mode's normal
    // rate and fast sections use its fast rate. Without this floor, a global
    // speaker-pace ceiling can collapse both categories to one speed.
    const modeFloor = Object.prototype.hasOwnProperty.call(PLAYBACK_MODES, mode) ? requested : null;
    if (modeFloor && requested >= modeFloor && speed < modeFloor) {
      const floorRate = supportedRates.find((rate) => rate >= modeFloor && rate <= requested);
      if (floorRate) speed = floorRate;
    }
    return { ...segment, speed, paceCapped: speed < requested - 0.01 };
  });
  return { ...plan, segments, estimatedDuration: calculateAdaptiveDuration(segments, { skipSponsors: true }) };
}

export function findSegmentIndex(segments, time) {
  if (!segments?.length) return -1;
  const index = segments.findIndex((segment, segmentIndex) =>
    time >= segment.start && (time < segment.end || (segmentIndex === segments.length - 1 && time <= segment.end)),
  );
  if (index >= 0) return index;
  return time < segments[0].start ? 0 : segments.length - 1;
}

export function resolveSegmentRate(segments, segmentIndex, temporaryOverride = null) {
  const segment = segments?.[segmentIndex];
  if (!segment) return null;
  if (temporaryOverride?.segmentIndex === segmentIndex && Number.isFinite(temporaryOverride.rate)) {
    return temporaryOverride.rate;
  }
  return segment.speed;
}

export function findSponsorSkipEnd(segments, currentIndex) {
  if (!segments?.[currentIndex]?.sponsor) return null;
  let end = segments[currentIndex].end;
  for (let index = currentIndex + 1; index < segments.length; index += 1) {
    const segment = segments[index];
    if (!segment.sponsor || segment.start > end + 0.05) break;
    end = Math.max(end, segment.end);
  }
  return end;
}

export function calculateAdaptiveRemaining(segments, currentTime) {
  const index = findSegmentIndex(segments, currentTime);
  if (index < 0) return 0;
  return segments.slice(index).reduce((total, segment, offset) => {
    const start = offset === 0 ? Math.max(segment.start, currentTime) : segment.start;
    return total + Math.max(0, segment.end - start) / segment.speed;
  }, 0);
}
